'use client'

// Hoja "Si no hay <ítem>" por línea del carrito (spec sustituciones §4.2, Task
// 4). Mismo patrón visual que las demás hojas del micrositio (ItemModal,
// AddressSheet, UpsellSheet): overlay fixed + panel anclado abajo.

import { useState } from 'react'
import { X, Check } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { CartItem } from '../types'
import { lineAllowsChosenSubstitute, type CandidateItem } from '@/lib/microsite/substitutionCandidates'
import type { SubstitutionPref } from '@/lib/online-orders/substitutionPrefs'
import type { PricingConfig } from '@/lib/utils/pricing'
import { effectiveItemCardPrice } from '@/lib/utils/pricing'
import { SubstitutionPrefSelector } from './SubstitutionPrefSelector'

type Props = {
  open: boolean
  onClose: () => void
  line: CartItem
  candidates: CandidateItem[]
  contactAllowed: boolean
  /** Ver SubstitutionPrefSelector: el teléfono se pide después del carrito. */
  contactPhonePending?: boolean
  accentColor: string
  onSave: (pref: SubstitutionPref | undefined, itemId: string | undefined) => void
  /** NORMA DE DINERO: `candidates[].price` es el precio CARD crudo que guarda
   *  `items.price` — mostrarlo tal cual ignora `dual_pricing_enabled` /
   *  `round_card_prices` del local. La UI muestra el precio EFECTIVO. */
  pricingCfg?: PricingConfig
}

export function SubstitutionLineSheet({ open, onClose, line, candidates, contactAllowed, contactPhonePending = false, accentColor, onSave, pricingCfg }: Props) {
  const t = useTranslations('public.onlineOrder.checkout.substitution')
  // undefined = hereda la preferencia de la orden entera.
  const [pref, setPref] = useState<SubstitutionPref | undefined>(line.substitutionPref)
  const [itemId, setItemId] = useState<string | undefined>(line.substitutionItemId)

  if (!open) return null

  const inheriting = pref === undefined && !itemId

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{t('lineTitle', { name: line.name })}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            aria-label={t('cancel')}
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          <button
            type="button"
            aria-pressed={inheriting}
            onClick={() => {
              setPref(undefined)
              setItemId(undefined)
            }}
            className="w-full flex items-center gap-3 rounded-2xl border-[1.5px] p-3.5 text-left transition"
            style={{
              borderColor: inheriting ? accentColor : '#E5E7EB',
              backgroundColor: inheriting ? `${accentColor}0D` : '#FFFFFF',
            }}
          >
            <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900">{t('inherit')}</span>
            <span
              className="w-[19px] h-[19px] flex-shrink-0 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: inheriting ? accentColor : 'transparent',
                border: inheriting ? 'none' : '1.5px solid #D1D5DB',
              }}
            >
              {inheriting && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </span>
          </button>

          <SubstitutionPrefSelector
            value={pref}
            onChange={(v) => {
              setPref(v)
              setItemId(undefined)
            }}
            contactAllowed={contactAllowed}
            contactPhonePending={contactPhonePending}
            accentColor={accentColor}
            compact
          />

          {/* Súper online (I1): una línea por peso o con variante solo lleva la
              preferencia; el plan B concreto no sabe de peso ni de variantes. */}
          {lineAllowsChosenSubstitute(line) && (
          <div>
            <p className="text-sm font-semibold text-gray-900">{t('choose')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('chooseHint')}</p>
            {candidates.length === 0 ? (
              <p className="mt-3 text-xs text-gray-500">{t('noCandidates')}</p>
            ) : (
              <div className="mt-2 space-y-2">
                {candidates.map((c) => {
                  const selected = itemId === c.id
                  const displayPrice = pricingCfg ? effectiveItemCardPrice(c.price, pricingCfg) : c.price
                  return (
                    <button
                      key={c.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setItemId(c.id)
                        setPref('similar')
                      }}
                      className="w-full flex items-center justify-between gap-3 rounded-2xl border-[1.5px] p-3.5 text-left transition"
                      style={{
                        borderColor: selected ? accentColor : '#E5E7EB',
                        backgroundColor: selected ? `${accentColor}0D` : '#FFFFFF',
                      }}
                    >
                      <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">{c.name}</span>
                      <span className="flex-shrink-0 text-sm font-bold text-gray-900">${displayPrice.toFixed(2)}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          )}
        </div>

        <div
          className="flex items-center gap-2.5 border-t border-gray-100 bg-white px-5 py-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full py-3 text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(pref, itemId)
              onClose()
            }}
            className="flex-1 rounded-full py-3 text-sm font-bold text-white transition hover:opacity-95"
            style={{ backgroundColor: accentColor }}
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  )
}
